import React from "react";
import { Formik, Form, Field, FormikProps } from "formik";
import { Input, Label, NativeSelect } from "@altimateai/lego";
import styles from "./styles.module.scss";
import { WarehouseSizeMap } from "../scheduler/helper";
import dayjs from "dayjs";
import * as Yup from "yup";
import { InfoIcon } from "@/assets/icons";
import { Tooltip, Stack } from "@altimateai/lego";
import { FormValues } from "./formConfig";

interface CommonScheduleFormProps {
  handleSubmit: (values: FormValues) => void;
  initialValues: FormValues;
  title?: string;
  warehouseSize?: string;
  readOnly?: boolean;
  formikRef?: React.Ref<FormikProps<FormValues>>;
  children?: (props: { values: FormValues }) => React.ReactNode;
  isEdit?: boolean;
  defaultWarehouseSize?: string;
}

// Define the order of warehouse sizes
const warehouseSizeOrder = [
  "X-Small",
  "Small",
  "Medium",
  "Large",
  "X-Large",
  "2X-Large",
  "3X-Large",
  "4X-Large",
  "5X-Large",
  "6X-Large",
];

const validationSchema = Yup.object({
  startDate: Yup.date().required("Start date is required."),
  startTime: Yup.string().required("Start time is required."),
  endDate: Yup.date()
    .nullable()
    .test("is-after-start", "End date cannot be earlier than start date.", function (value) {
      const { startDate } = this.parent;
      return value ? !dayjs(value).isBefore(dayjs(startDate)) : true;
    }),
  endTime: Yup.string()
    .nullable()
    .test(
      "is-after-start-time",
      "End time cannot be earlier than or equal to start time.",
      function (value) {
        const { startDate, endDate, startTime } = this.parent;

        if (!startDate || !endDate || !startTime || !value) {
          return true; // Skip validation if any required field is missing
        }

        const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
        const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");

        if (formattedStartDate === formattedEndDate) {
          const start = dayjs(`${formattedStartDate}T${startTime}`);
          const end = dayjs(`${formattedEndDate}T${value}`);

          return end.isAfter(start); // Check if end time is after start time
        }

        return true; // No validation needed if dates are different
      }
    ),
});

export const CommonScheduleForm: React.FC<CommonScheduleFormProps> = ({
  handleSubmit,
  initialValues,
  title,
  warehouseSize,
  readOnly,
  formikRef,
  children,
  defaultWarehouseSize,
}) => (
  <Formik
    initialValues={initialValues}
    onSubmit={handleSubmit}
    innerRef={formikRef}
    validationSchema={validationSchema}
  >
    {({ values, errors, touched, setFieldValue }) => (
      <Form className={styles.formContainer} role="form">
        <div className="pb-4">
          {title && (
            <div className="al-grid al-grid-cols-1">
              <h4>{title}</h4>
            </div>
          )}
        </div>
        <div className="al-grid al-grid-cols-1 md:al-grid-cols-2 al-gap-4">
          <div className={styles.formCol}>
            <div>
              <Label htmlFor="startDate">Start Date</Label>
              <Field
                as={Input}
                type="date"
                name="startDate"
                id="startDate"
                className={styles.formField}
                disabled={readOnly}
                value={dayjs(values.startDate).format("YYYY-MM-DD")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue("startDate", e.target.value);
                  if (dayjs(e.target.value).isAfter(dayjs(values.endDate))) {
                    setFieldValue("endDate", null); // Reset endDate
                  }
                }}
              ></Field>
            </div>
          </div>
          <div className={styles.formCol}>
            <div>
              <Label htmlFor="startTime">Start Time</Label>
              <Field
                as={Input}
                type="time"
                name="startTime"
                id="startTime"
                step="900"
                className={styles.formField}
                disabled={readOnly}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue("startTime", e.target.value);
                  if (values.startDate === values.endDate && e.target.value > values.endTime) {
                    setFieldValue("endTime", ""); // Reset endTime
                  }
                }}
              />
            </div>
          </div>
        </div>
        <div className="al-grid al-grid-cols-1 md:al-grid-cols-2 al-gap-4">
          <div className={styles.formCol}>
            <div>
              <Label htmlFor="endDate">End Date</Label>
              <Field
                as={Input}
                type="date"
                name="endDate"
                id="endDate"
                className={`${styles.formField} ${
                  errors.endDate && touched.endDate ? styles.errorField : ""
                }`}
                disabled={!values.startDate || readOnly}
                value={dayjs(values.endDate).format("YYYY-MM-DD")}
              ></Field>
              {errors.endDate && touched.endDate && (
                <div className={styles.errorText}>{errors.endDate as string}</div>
              )}
            </div>
          </div>
          <div className={styles.formCol}>
            <div>
              <Label htmlFor="endTime">End Time</Label>
              <Field
                as={Input}
                type="time"
                name="endTime"
                id="endTime"
                step="900"
                className={`${styles.formField} ${
                  errors.endTime && touched.endTime ? styles.errorField : ""
                }`}
                disabled={!values.startTime || readOnly}
              />
              {errors.endTime && touched.endTime && (
                <div className={styles.errorText}>{errors.endTime as string}</div>
              )}
            </div>
          </div>
        </div>

        {values.scheduleBlocker !== "Yes" && (
          <>
            <div className="al-grid al-grid-cols-1 md:al-grid-cols-2 al-gap-4">
              <div className={styles.formCol}>
                <div>
                  <Label htmlFor="currentWarehouseSize">Default Warehouse Size</Label>
                  <Field
                    as={Input}
                    type="text"
                    name="currentWarehouseSize"
                    id="currentWarehouseSize"
                    disabled
                    className={styles.formField}
                  />
                </div>
              </div>
              <div className={styles.formCol}>
                <div>
                  <Label htmlFor="warehouseChangeTo">Change Warehouse Size To</Label>
                  <NativeSelect
                    buttonProps={{ "aria-label": "Select warehouse size" }}
                    name="warehouseChangeTo"
                    id="warehouseChangeTo"
                    className={styles.formField}
                    disabled={readOnly}
                    placeholder="Select Size"
                    value={values.warehouseChangeTo}
                    variant="outline"
                    onChange={(value: string) => {
                      setFieldValue("warehouseChangeTo", value);
                    }}
                    options={Object.keys(WarehouseSizeMap)
                      .filter(size => size !== defaultWarehouseSize && size !== warehouseSize)
                      .sort((a, b) => warehouseSizeOrder.indexOf(a) - warehouseSizeOrder.indexOf(b))
                      .map((size: string) => ({
                        label: size,
                        value: size,
                      }))}
                  />
                </div>
              </div>
            </div>
            {/* <Row>
              <Col md={6} className={styles.formCol}>
                <div>
                  <Label for="clusterMin">Cluster Min</Label>
                  <Field
                    as={Input}
                    type="number"
                    name="clusterMin"
                    id="clusterMin"
                    className={styles.formField}
                    disabled={readOnly}
                  />
                </div>
              </Col>
              <Col md={6} className={styles.formCol}>
                <div>
                  <Label for="clusterMax">Cluster Max</Label>
                  <Field
                    as={Input}
                    type="number"
                    name="clusterMax"
                    id="clusterMax"
                    className={styles.formField}
                    disabled={readOnly}
                  />
                </div>
              </Col>
            </Row> */}
          </>
        )}

        <div className="al-grid al-grid-cols-1">
          <div className={styles.formCol}>
            <div className={styles.formGroup}>
              <Stack align="center">
                <Label htmlFor="scheduleBlocker">
                  <Stack align="center">
                    Schedule Blocker{" "}
                    <Tooltip title="If marked yes, this time window will be blocked so no recommended or custom schedule created in this time window">
                      <Stack>
                        <InfoIcon />
                      </Stack>
                    </Tooltip>
                  </Stack>
                </Label>
              </Stack>
              <NativeSelect
                buttonProps={{ "aria-label": "Select schedule blocker" }}
                name="scheduleBlocker"
                id="scheduleBlocker"
                className={styles.formField}
                disabled={readOnly}
                placeholder="Select"
                variant="outline"
                value={values.scheduleBlocker}
                onChange={(value: string) => {
                  setFieldValue("scheduleBlocker", value);
                }}
                options={[
                  { label: "No", value: "No" },
                  { label: "Yes", value: "Yes" },
                ]}
              />
            </div>
          </div>
        </div>

        {/* <Row>
          <Col md={6} className={styles.formCol}>
            <div>
              <Label for="repeat">Repeat</Label>
              <Field
                as={Input}
                type="number"
                name="repeat"
                id="repeat"
                className={styles.formField}
                disabled={readOnly}
              />
            </div>
          </Col>
          <Col md={6} className={styles.formCol}>
            <div>
              <Label for="repeatUnit">Repeat Unit</Label>
              <Field
                as={Input}
                type="select"
                name="repeatUnit"
                id="repeatUnit"
                className={styles.formField}
                disabled={readOnly}
              >
                <option>Day</option>
                <option>Week</option>
                <option>Month</option>
              </Field>
            </div>
          </Col>
        </Row> */}

        {/* <FormGroup className={styles.formGroup}>
          <WeekdaySelector
            name="repeatOn"
            label="Repeat On"
            initialValues={initialValues.repeatOn}
          />
        </FormGroup>

        <FormGroup className={styles.formGroup}>
          <Label>Ends</Label>
          <div className={styles.endsContainer}>
            <Label check className={styles.radioLabel}>
              <Field
                type="radio"
                name="ends"
                value="Never"
                className={styles.formRadio}
                disabled={readOnly}
              />{" "}
              Never
            </Label>
            <Label check className={styles.radioLabel}>
              <Field
                type="radio"
                name="ends"
                value="On"
                className={styles.formRadio}
                disabled={readOnly}
              />
              <span>On</span>
              <Field
                as={Input}
                type="date"
                name="endsOnDate"
                id="endsOnDate"
                className={styles.formField}
                disabled={readOnly}
              />
            </Label>
            <Label check className={styles.radioLabel}>
              <Field
                type="radio"
                name="ends"
                value="After"
                className={styles.formRadio}
                disabled={readOnly}
              />{" "}
              After
              <Field
                as={Input}
                type="number"
                name="endsAfterOccurrences"
                id="endsAfterOccurrences"
                className={styles.formField}
                disabled={readOnly}
              />
            </Label>
          </div>
        </FormGroup> */}
        {children && children({ values })}
      </Form>
    )}
  </Formik>
);
